import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "title", "message", "queued"]
  static values = {
    duration: { type: Number, default: 2600 },
    successTitle: { type: String, default: "Success" },
    errorTitle: { type: String, default: "Error" },
    infoTitle: { type: String, default: "Notice" },
  }

  connect() {
    this.queue = []
    this.isShowing = false
    this.boundWindowToast = this.onWindowToast.bind(this)
    window.addEventListener("toast:show", this.boundWindowToast)
    this.loadFlashQueue()
    this.showNext()
  }

  disconnect() {
    if (this.timeout) window.clearTimeout(this.timeout)
    if (this.hideTimer) window.clearTimeout(this.hideTimer)
    window.removeEventListener("toast:show", this.boundWindowToast)
  }

  show(event) {
    const message = event?.params?.message || "Saved."
    this.enqueue({
      message,
      kind: this.normalizeKind(event?.params?.kind || "success"),
      title: event?.params?.title,
    })
    this.showNext()
  }

  onWindowToast(event) {
    const message = event?.detail?.message
    if (!message) return

    this.enqueue({
      message,
      kind: this.normalizeKind(event?.detail?.kind),
      title: event?.detail?.title,
    })
    this.showNext()
  }

  hide() {
    if (!this.hasContainerTarget) return
    if (this.timeout) window.clearTimeout(this.timeout)
    this.hideCurrent()
  }

  loadFlashQueue() {
    if (!this.hasQueuedTarget) return

    this.queuedTargets.forEach((node) => {
      this.processQueuedNode(node)
    })
  }

  queuedTargetConnected(node) {
    this.processQueuedNode(node)
    this.showNext()
  }

  processQueuedNode(node) {
    const message = node.dataset.toastMessage
    if (!message) return

    this.enqueue({
      message,
      kind: this.normalizeKind(node.dataset.toastKind),
      title: node.dataset.toastTitle,
    })
  }

  enqueue(payload) {
    this.queue.push(payload)
  }

  showNext() {
    if (this.isShowing) return
    if (!this.hasContainerTarget || !this.hasMessageTarget) return

    const next = this.queue.shift()
    if (!next) return

    this.isShowing = true
    this.render(next)
    this.scheduleHide()
  }

  render({ message, kind, title }) {
    const resolvedKind = this.normalizeKind(kind)
    const resolvedTitle = title || this.defaultTitle(resolvedKind)

    this.containerTarget.dataset.toastKind = resolvedKind
    this.messageTarget.textContent = message

    if (this.hasTitleTarget) {
      this.titleTarget.hidden = !resolvedTitle
      this.titleTarget.textContent = resolvedTitle
    }

    this.containerTarget.hidden = false

    requestAnimationFrame(() => {
      this.containerTarget.classList.add("is-visible")
    })
  }

  scheduleHide() {
    if (this.timeout) window.clearTimeout(this.timeout)
    this.timeout = window.setTimeout(() => this.hideCurrent(), this.durationValue)
  }

  hideCurrent() {
    this.containerTarget.classList.remove("is-visible")
    if (this.hideTimer) window.clearTimeout(this.hideTimer)

    this.hideTimer = window.setTimeout(() => {
      this.containerTarget.hidden = true
      this.isShowing = false
      this.showNext()
    }, 220)
  }

  normalizeKind(kind) {
    if (kind === "error" || kind === "success") return kind
    return "info"
  }

  defaultTitle(kind) {
    if (kind === "success") return this.successTitleValue
    if (kind === "error") return this.errorTitleValue
    return this.infoTitleValue
  }
}
